/** DOM id of the print-page content root — the element the headless renderer measures. */
export const DOWNLOAD_ROOT_ID = 'download-root';

const STABLE_FRAMES = 3;
const STABLE_TIMEOUT_MS = 3000;

/**
 * Resolve once the content height stops changing. Late images and open-animations
 * keep growing the page for several frames after mount, and the headless renderer
 * measures `scrollHeight` the moment `__PDF_READY__` flips — so a premature flag
 * clips the bottom. Polls until the height holds for `STABLE_FRAMES`, capped by
 * `STABLE_TIMEOUT_MS` so it can never hang a render.
 *
 * A height of zero never counts as stable. Registry entries render behind
 * `Suspense` with a `null` fallback, so between the data resolving and the lazy
 * chunk mounting the root is empty — and an empty root holds 0px just as steadily
 * as a finished one holds its final height. Treating that as settled flips the
 * flag against nothing, and the renderer fails on a 0×0 measurement instead of
 * waiting the few extra frames the chunk needed.
 */
export const waitForStableHeight = (): Promise<void> =>
  new Promise((resolve) => {
    const start = performance.now();
    let lastHeight = -1;
    let stableCount = 0;

    const tick = () => {
      const root = document.getElementById(DOWNLOAD_ROOT_ID);
      const height = root ? root.scrollHeight : 0;

      if (height > 0 && height === lastHeight) {
        stableCount += 1;
      } else {
        stableCount = 0;
        lastHeight = height;
      }

      if (stableCount >= STABLE_FRAMES || performance.now() - start >= STABLE_TIMEOUT_MS) {
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  });
